import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { CompletedQuizData, Quiz, QuizQuestion, UpcomingQuizData, UserStats } from "~/interface/satsang-quiz/satsangQuiz";
import API_PATH from "~/utils/api-routes";
import { AUTH_COOKIE_TOKEN } from "~/utils/constant";
import { getCookie } from "~/utils/cookies";
import api from "~/utils/Interceptor";
import { createSmkApi, setLatestOtpToken } from "~/utils/smkInterseptor";
export type Mode = "mobile" | "desktop";
interface initialStateData {
  quizData: Quiz | null;
  stats: UserStats | null;
  upcomingQuizData: UpcomingQuizData[];
  completedQuizData: CompletedQuizData[];
  runningQuizData: QuizQuestion[];
  totalPoints: number; // Optional: if you want to track total points in the state
  currentQuestion: number; // Optional: if you want to track the current operation
  timePerQuestion: number; // Optional: if you want to track time per question
  pointsPerQuestion: number; // Optional: if you want to track points per question
  is_live: boolean; // Optional: if you want to track if the quiz is live
  quiz_start_Time: string | null; // Optional: if you want to track the quiz start time
  quizEnded: boolean; // Optional: if you want to track if the quiz has ended
  loading: boolean;
  error: string | null;
}

const initialState: initialStateData = {
  quizData: null,
  stats: null,
  upcomingQuizData: [],
  completedQuizData: [],
  runningQuizData: [],
  totalPoints: 0, // Initialize total points
  currentQuestion: 0, // Initialize current option
  timePerQuestion: 0, // Initialize time per question
  pointsPerQuestion: 0, // Initialize points per question
  is_live: false, // Initialize live status
  quiz_start_Time: null, // Initialize quiz start time
  quizEnded: false, // Initialize quiz ended state
  loading: false,
  error: null,
};

const smkService = createSmkApi();

//create getsatsangQuizData thunk
export const getSatsangQuizDataThunk = createAsyncThunk("satsangQuiz/getSatsangQuizData", async (_, { rejectWithValue }) => {
  try {
    const response = await api.get(API_PATH.QUIZ);
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<{ message: string }>;
    const message = err.response?.data?.message || err.message || "An error occurred while deleting member for non-SMK family";
    return rejectWithValue(message);
  }
});

//create states thunk
export const getUserStatsDataThunk = createAsyncThunk(
  "satsangQuiz/getUserStatsData",
  async ({ mode, tempToken }: { mode: Mode; tempToken: string | null }, { rejectWithValue }) => {
    try {
      const token = getCookie(AUTH_COOKIE_TOKEN);
      setLatestOtpToken(mode === "mobile" ? tempToken || "" : token || "");
      const response = await smkService.get(API_PATH.USER_STATS);
      return response.data.data;
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      const message = err.response?.data?.message || err.message || "An error occurred while deleting member for non-SMK family";
      return rejectWithValue(message);
    }
  },
);

//get upcoming quiz data thunk
export const getUpcomingQuizDataThunk = createAsyncThunk("satsangQuiz/getUpcomingQuizData", async (_, { rejectWithValue }) => {
  try {
    const response = await api.get(API_PATH.UPCOMING_QUIZ);
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<{ message: string }>;
    const message = err.response?.data?.message || err.message || "An error occurred while fetching upcoming quiz data";
    return rejectWithValue(message);
  }
});

//get completed quiz data thunk
export const getCompletedQuizDataThunk = createAsyncThunk<
  any,
  { type: "with_auth" | "without_auth"; mode: Mode; tempToken: string | null; pageSize?: number; pageIndex?: number }
>("satsangQuiz/getCompletedQuizData", async ({ type, mode, tempToken, pageSize = 10, pageIndex = 1 }, { rejectWithValue }) => {
  try {
    const token = getCookie(AUTH_COOKIE_TOKEN);
    setLatestOtpToken(mode === "mobile" ? tempToken || "" : token || "");
    const endpoint = type === "with_auth" ? API_PATH.COMPLETED_QUIZ_WITH_LOGIN : API_PATH.COMPLETED_QUIZ_WITHOUT_LOGIN;
    const response = await smkService.get(endpoint, {
      params: {
        pageSize,
        pageIndex,
      },
    });
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<{ message: string }>;
    const message = err.response?.data?.message || err.message || "An error occurred while fetching completed quiz data";
    return rejectWithValue(message);
  }
});

//get running quiz data thunk
export const getRunningQuizDataThunk = createAsyncThunk(
  "satsangQuiz/getRunningQuizData",
  async ({ quizId, mode, tempToken }: { quizId: number; mode: Mode; tempToken: string | null }, { rejectWithValue }) => {
    try {
      const token = getCookie(AUTH_COOKIE_TOKEN);
      setLatestOtpToken(mode === "mobile" ? tempToken || "" : token || "");
      const response = await smkService.get(`${API_PATH.QUIZ}/${quizId}`);
      return response.data.data;
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      const message = err.response?.data?.message || err.message || "An error occurred while fetching running quiz data";
      return rejectWithValue(message);
    }
  },
);

//submit quiz answers thunk
export const submitQuizAnswersThunk = createAsyncThunk(
  "satsangQuiz/submitQuizAnswers",
  async (
    payload: { mode: Mode; quiz_id: number; total_point: number; user_ans: { que_id: number; selected: string[] }[]; tempToken: string | null },
    { rejectWithValue },
  ) => {
    try {
      const { mode, tempToken, ...rest } = payload;
      const token = getCookie(AUTH_COOKIE_TOKEN);
      setLatestOtpToken(mode === "mobile" ? tempToken || "" : token || "");
      const response = await smkService.post(`${API_PATH.SUBMIT_QUIZ}`, rest);
      return response.data.data;
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      const message = err.response?.data?.message || err.message || "An error occurred while submitting quiz answers";
      return rejectWithValue(message);
    }
  },
);

const satsangQuizSlice = createSlice({
  name: "satsangQuiz",
  initialState,
  reducers: {
    setQuizData: (state, action: PayloadAction<Quiz | null>) => {
      state.quizData = action.payload;
    },

    setStats: (state, action: PayloadAction<UserStats | null>) => {
      state.stats = action.payload;
    },

    setTotalPoints: (state, action: PayloadAction<number>) => {
      state.totalPoints = action.payload;
    },

    setCurrentQuestion: (state, action: PayloadAction<number>) => {
      state.currentQuestion = action.payload;
    },
    setQuizEnded: (state, action: PayloadAction<boolean>) => {
      state.quizEnded = action.payload;
    },

    setPointPerQuestion: (state, action: PayloadAction<number>) => {
      state.pointsPerQuestion = action.payload;
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Handle the main thunk for fetching quiz data
    builder
      .addCase(getSatsangQuizDataThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSatsangQuizDataThunk.fulfilled, (state, action: PayloadAction<Quiz | null>) => {
        state.loading = false;
        state.quizData = action.payload;
      })
      .addCase(getSatsangQuizDataThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Handle the thunk for fetching user stats
    builder
      .addCase(getUserStatsDataThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserStatsDataThunk.fulfilled, (state, action: PayloadAction<UserStats | null>) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(getUserStatsDataThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Handle the additional thunks for upcoming quiz data
    builder
      .addCase(getUpcomingQuizDataThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUpcomingQuizDataThunk.fulfilled, (state, action: PayloadAction<{ count: number; rows: UpcomingQuizData[] }>) => {
        state.loading = false;
        state.upcomingQuizData = action.payload.rows;
      })
      .addCase(getUpcomingQuizDataThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Handle the additional thunk for completed quiz data
    builder
      .addCase(getCompletedQuizDataThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCompletedQuizDataThunk.fulfilled, (state, action: PayloadAction<{ count: number; rows: CompletedQuizData[] }>) => {
        state.loading = false;
        state.completedQuizData = action.payload.rows;
      })
      .addCase(getCompletedQuizDataThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Handle the additional thunk for running quiz data
    builder
      .addCase(getRunningQuizDataThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getRunningQuizDataThunk.fulfilled,
        (
          state,
          action: PayloadAction<{
            state_data: { is_live: boolean; time_per_que: number; point_per_que: number; start_time: string | null };
            questions: QuizQuestion[];
          }>,
        ) => {
          state.loading = false;
          state.runningQuizData = action.payload.questions;
          state.timePerQuestion = action.payload.state_data.time_per_que;
          state.pointsPerQuestion = action.payload.state_data.point_per_que;
          state.is_live = action.payload.state_data.is_live;
          state.quiz_start_Time = action.payload.state_data.start_time;
        },
      )
      .addCase(getRunningQuizDataThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Handle the thunk for submitting quiz answers
    builder
      .addCase(submitQuizAnswersThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        submitQuizAnswersThunk.fulfilled,
        (state, action: PayloadAction<{ total_points: number; user_ans: { que_id: number; selected: string[] }[] }>) => {
          state.loading = false;
        },
      )
      .addCase(submitQuizAnswersThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setQuizData, setStats, setTotalPoints, setCurrentQuestion, setQuizEnded, setPointPerQuestion, setLoading, setError } =
  satsangQuizSlice.actions;

export default satsangQuizSlice.reducer;
